import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  LifeBuoy, Plus, X, AlertTriangle, MessageSquare, Send, CheckCircle2, RotateCcw,
  Cpu, Wifi, Mail as MailIcon, Phone as PhoneIcon, Lock, AppWindow, HelpCircle, Monitor,
} from "lucide-react";

const CATEGORIES = [
  { value: "hardware", label: "Hardware", icon: Cpu },
  { value: "software", label: "Software", icon: Monitor },
  { value: "account_access", label: "Account access", icon: Lock },
  { value: "network_wifi", label: "Network / Wi-Fi", icon: Wifi },
  { value: "email", label: "Email", icon: MailIcon },
  { value: "phone", label: "Phone / mobile", icon: PhoneIcon },
  { value: "applications_systems", label: "Applications / systems", icon: AppWindow },
  { value: "other", label: "Other", icon: HelpCircle },
];

const PRIORITIES = [
  { value: "low", label: "Low", cls: "bg-[#F1F5F9] text-[#475569]" },
  { value: "medium", label: "Medium", cls: "bg-[#E0E7FF] text-[#1E3A8A]" },
  { value: "high", label: "High", cls: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "critical", label: "Critical", cls: "bg-[#FEE2E2] text-[#991B1B]" },
];

const STATUSES = [
  { value: "open", label: "Open", cls: "bg-[#FEF3C7] text-[#92400E]" },
  { value: "in_progress", label: "In progress", cls: "bg-[#E0E7FF] text-[#1E3A8A]" },
  { value: "waiting_on_user", label: "Waiting on user", cls: "bg-[#FCE7F3] text-[#9D174D]" },
  { value: "resolved", label: "Resolved", cls: "bg-[#E8ECE7] text-[#166534]" },
  { value: "closed", label: "Closed", cls: "bg-[#F1F5F9] text-[#475569]" },
];

const PRI = Object.fromEntries(PRIORITIES.map((p) => [p.value, p]));
const STAT = Object.fromEntries(STATUSES.map((s) => [s.value, s]));
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

export default function TechSupport() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(params.get("new") === "1");
  const [selectedId, setSelectedId] = useState(params.get("id") || null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [mineOnly, setMineOnly] = useState(false);

  const isITAdmin = user && (user.role === "admin" || user.role === "executive_director" || user.is_it_support);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ticketsRes = await api.get("/tickets");
      setTickets(ticketsRes.data || []);
      // Only IT admins can read /users — non-admins don't need it (they
      // can't reassign tickets anyway) and would otherwise get a 403
      // toast. Skip the call instead of swallowing the error.
      if (isITAdmin) {
        try {
          const usersRes = await api.get("/users");
          setStaffUsers(usersRes.data || []);
        } catch (e) {
          // Soft-fail: assignee names just render as "Unknown".
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [isITAdmin]);
  useEffect(() => { load(); }, [load]);

  const userById = useMemo(() => Object.fromEntries(staffUsers.map((u) => [u.id, u])), [staffUsers]);
  const userName = (id) => userById[id]?.name || (id ? "Unknown" : "Unassigned");

  const visible = useMemo(() => {
    return tickets.filter((t) => {
      if (mineOnly && t.created_by !== user?.id) return false;
      if (statusFilter === "active") return t.status !== "resolved" && t.status !== "closed";
      if (statusFilter === "resolved") return t.status === "resolved" || t.status === "closed";
      if (statusFilter === "all") return true;
      return t.status === statusFilter;
    });
  }, [tickets, mineOnly, statusFilter, user]);

  const selected = selectedId ? tickets.find((t) => t.id === selectedId) : null;

  return (
    <div className="space-y-6" data-testid="tech-support-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Get help fast</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Tech Support</h1>
          <p className="text-sm text-[#334155] mt-1">
            Stuck on something tech-related? Log a ticket and our IT team will jump in.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="new-ticket-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
        >
          <Plus className="w-4 h-4" /> New ticket
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex p-1 rounded-full bg-white border border-[#E5E5E0]">
          {[
            { v: "active", label: "Active" },
            { v: "open", label: "Open" },
            { v: "in_progress", label: "In progress" },
            { v: "waiting_on_user", label: "Waiting" },
            { v: "resolved", label: "Resolved" },
            { v: "all", label: "All" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              data-testid={`ticket-filter-${s.v}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s.v ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#334155] ml-2">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
            data-testid="ticket-filter-mine"
            className="rounded"
          />
          Only the ones I logged
        </label>
      </div>

      {/* Body: list + detail */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1">
          {loading ? (
            <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E5E5E0] p-8 text-center" data-testid="tickets-empty">
              <LifeBuoy className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#334155] font-medium">No tickets here</p>
              <p className="text-sm text-[#1E3A8A] mt-1">Click <strong>New ticket</strong> to log one.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="tickets-list">
              {visible.map((t) => {
                const pri = PRI[t.priority] || PRI.medium;
                const stat = STAT[t.status] || STAT.open;
                const Cat = (CAT[t.category] || CAT.other).icon;
                const isSelected = selectedId === t.id;
                return (
                  <li
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    data-testid={`ticket-row-${t.id}`}
                    className={`cursor-pointer rounded-2xl p-3.5 transition-colors border ${
                      isSelected ? "border-[#1E3A8A] bg-[#FAFBFF]" : "border-[#E5E5E0] bg-white hover:bg-[#F9F8F6]"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                        <Cat className="w-4 h-4" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase bg-[#E0E7FF] px-1.5 py-0.5 rounded-full">
                            {t.display_id}
                          </span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider ${pri.cls}`}>{pri.label}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider ${stat.cls}`}>{stat.label}</span>
                        </div>
                        <h3 className="font-medium text-[#0F172A] text-sm truncate">{t.title}</h3>
                        <p className="text-xs text-[#334155] mt-0.5 truncate">
                          Logged by {t.is_external ? `${t.reporter_name} (external)` : userName(t.created_by)} · {formatDateTime(t.created_at)}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <TicketDetail
              ticket={selected}
              isITAdmin={!!isITAdmin}
              currentUserId={user?.id}
              staffUsers={staffUsers}
              userName={userName}
              onChanged={load}
              onDeleted={() => { setSelectedId(null); load(); }}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-[#E5E5E0] p-10 text-center self-start" data-testid="ticket-detail-empty">
              <LifeBuoy className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[#334155] font-medium">Pick a ticket from the list</p>
              <p className="text-sm text-[#1E3A8A] mt-1">Or click <strong>New ticket</strong> to log a fresh issue.</p>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <NewTicketDialog
          staffUsers={staffUsers}
          isITAdmin={!!isITAdmin}
          onClose={() => setShowForm(false)}
          onCreated={(t) => { setShowForm(false); load(); setSelectedId(t.id); }}
        />
      )}
    </div>
  );
}

function TicketDetail({ ticket, isITAdmin, currentUserId, staffUsers, userName, onChanged, onDeleted }) {
  const t = ticket;
  const canEdit = isITAdmin || t.assignee_id === currentUserId || t.created_by === currentUserId;
  const canReassign = isITAdmin || t.assignee_id === currentUserId;
  const Cat = (CAT[t.category] || CAT.other).icon;
  const [commentBody, setCommentBody] = useState("");
  const [savingComment, setSavingComment] = useState(false);

  const patch = async (update, successMsg) => {
    try {
      await api.patch(`/tickets/${t.id}`, update);
      if (successMsg) toast.success(successMsg);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    const body = commentBody.trim();
    if (!body) return;
    setSavingComment(true);
    try {
      await api.post(`/tickets/${t.id}/comments`, { body });
      setCommentBody("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ticket ${t.display_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      toast.success("Ticket deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const itStaff = staffUsers.filter((u) => u.active && (u.role === "admin" || u.role === "executive_director" || u.is_it_support));

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E0] p-5 sm:p-6 space-y-5" data-testid="ticket-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
            <Cat className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase bg-[#E0E7FF] px-2 py-0.5 rounded-full">
                {t.display_id}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${(PRI[t.priority] || PRI.medium).cls}`}>
                {(PRI[t.priority] || PRI.medium).label}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${(STAT[t.status] || STAT.open).cls}`}>
                {(STAT[t.status] || STAT.open).label}
              </span>
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">{t.title}</h2>
            <p className="text-xs text-[#1E3A8A] mt-0.5">
              {t.is_external ? (
                <>
                  <span className="text-[10px] font-mono uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded-full mr-1.5">External</span>
                  {t.reporter_name}
                  {t.reporter_email && (
                    <> · <a href={`mailto:${t.reporter_email}`} className="hover:underline">{t.reporter_email}</a></>
                  )}
                  {t.reporter_phone && <> · {t.reporter_phone}</>}
                  {t.reporter_organization && <> · {t.reporter_organization}</>}
                </>
              ) : (
                <>Logged by <strong className="text-[#334155]">{userName(t.created_by)}</strong></>
              )}
              {" · "}{formatDateTime(t.created_at)}
            </p>
          </div>
        </div>
        {isITAdmin && (
          <button
            onClick={handleDelete}
            data-testid={`delete-ticket-${t.id}`}
            className="text-xs text-[#991B1B] inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-[#FEE2E2]"
          >
            <X className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>

      {/* Property strip */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 rounded-2xl bg-[#F9F8F6] p-4 border border-[#E5E5E0]">
        <PropRow label="Category" disabled={!canEdit}>
          <select
            value={t.category}
            disabled={!canEdit}
            onChange={(e) => patch({ category: e.target.value }, "Category updated")}
            data-testid={`field-category-${t.id}`}
            className="w-full bg-transparent text-sm text-[#0F172A] font-medium disabled:opacity-100 disabled:cursor-default focus:outline-none"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </PropRow>
        <PropRow label="Priority" disabled={!canEdit}>
          <select
            value={t.priority}
            disabled={!canEdit}
            onChange={(e) => patch({ priority: e.target.value }, "Priority updated")}
            data-testid={`field-priority-${t.id}`}
            className="w-full bg-transparent text-sm text-[#0F172A] font-medium disabled:opacity-100 disabled:cursor-default focus:outline-none"
          >
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </PropRow>
        <PropRow label="Status" disabled={!canEdit}>
          <select
            value={t.status}
            disabled={!canEdit}
            onChange={(e) => patch({ status: e.target.value }, "Status updated")}
            data-testid={`field-status-${t.id}`}
            className="w-full bg-transparent text-sm text-[#0F172A] font-medium disabled:opacity-100 disabled:cursor-default focus:outline-none"
          >
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </PropRow>
        <PropRow label="Assignee" disabled={!canReassign}>
          <select
            value={t.assignee_id || ""}
            disabled={!canReassign}
            onChange={(e) => patch({ assignee_id: e.target.value || null }, "Reassigned")}
            data-testid={`field-assignee-${t.id}`}
            className="w-full bg-transparent text-sm text-[#0F172A] font-medium disabled:opacity-100 disabled:cursor-default focus:outline-none"
          >
            <option value="">Unassigned</option>
            {itStaff.map((u) => <option key={u.id} value={u.id}>{u.name}{u.is_it_support ? " (IT)" : ""}</option>)}
          </select>
        </PropRow>
      </div>

      {/* Description */}
      {t.description && (
        <div>
          <div className="hw-eyebrow text-[10px] mb-2">Description</div>
          <p className="text-sm text-[#334155] whitespace-pre-wrap">{t.description}</p>
        </div>
      )}

      {(t.affected_device || t.affected_url) && (
        <div className="flex flex-wrap gap-4 text-xs text-[#334155]">
          {t.affected_device && (
            <span className="inline-flex items-center gap-1.5">
              <Monitor className="w-3.5 h-3.5 text-[#1E3A8A]" strokeWidth={1.75} />
              {t.affected_device}
            </span>
          )}
          {t.affected_url && (
            <a href={t.affected_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#1E3A8A] hover:underline">
              <AppWindow className="w-3.5 h-3.5" strokeWidth={1.75} />
              {t.affected_url}
            </a>
          )}
        </div>
      )}

      {/* Resolution */}
      {(t.status === "resolved" || t.status === "closed" || t.resolution) && (
        <div className="rounded-xl bg-[#E8ECE7] p-4 border border-[#BBF7D0]">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-[#166534]" strokeWidth={1.75} />
            <span className="text-xs font-medium text-[#166534] uppercase tracking-wider">Resolution</span>
          </div>
          {canEdit ? (
            <textarea
              defaultValue={t.resolution || ""}
              rows={2}
              placeholder="How was it resolved? (saved on blur)"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== t.resolution) patch({ resolution: v }, "Resolution saved");
              }}
              data-testid={`field-resolution-${t.id}`}
              className="w-full bg-white rounded-lg border border-[#BBF7D0] px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#166534]"
            />
          ) : (
            <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{t.resolution || "—"}</p>
          )}
        </div>
      )}

      {/* Comments */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
          <span className="text-xs font-medium text-[#334155] uppercase tracking-wider">
            Conversation {t.comments && t.comments.length > 0 ? `· ${t.comments.length}` : ""}
          </span>
        </div>
        <ul className="space-y-2 mb-3" data-testid={`comments-${t.id}`}>
          {(t.comments || []).map((c) => (
            <li key={c.id} className="rounded-xl bg-[#F9F8F6] p-3 border border-[#E5E5E0]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-[#1E3A8A]">{c.author_name}</span>
                <span className="text-[10px] text-[#475569]">{formatDateTime(c.created_at)}</span>
              </div>
              <p className="text-sm text-[#334155] whitespace-pre-wrap">{c.body}</p>
            </li>
          ))}
          {(!t.comments || t.comments.length === 0) && (
            <li className="text-xs text-[#1E3A8A] py-2">No replies yet — start the conversation below.</li>
          )}
        </ul>

        <form onSubmit={handleComment} className="flex gap-2" data-testid={`comment-form-${t.id}`}>
          <input
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            placeholder="Add a comment, status update, or question…"
            data-testid={`comment-input-${t.id}`}
            className="flex-1 px-3 py-2.5 rounded-full border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
          />
          <button
            type="submit"
            disabled={savingComment || !commentBody.trim()}
            data-testid={`comment-submit-${t.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
          >
            <Send className="w-4 h-4" strokeWidth={1.75} />
            Reply
          </button>
        </form>
      </div>

      {/* Quick actions for IT */}
      {isITAdmin && t.status !== "closed" && (
        <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E5E5E0]">
          {t.status !== "resolved" && (
            <button
              onClick={() => patch({ status: "resolved" }, "Marked resolved")}
              data-testid={`quick-resolve-${t.id}`}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8ECE7] text-[#166534] font-medium hover:bg-[#dfe6dc]"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
            </button>
          )}
          {t.status === "resolved" && (
            <button
              onClick={() => patch({ status: "open" }, "Reopened")}
              data-testid={`quick-reopen-${t.id}`}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-medium hover:bg-[#FDE68A]"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PropRow({ label, disabled, children }) {
  return (
    <div className={`rounded-xl bg-white p-3 border border-[#E5E5E0] ${disabled ? "opacity-90" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#1E3A8A] mb-1">{label}</div>
      {children}
    </div>
  );
}

function NewTicketDialog({ staffUsers, isITAdmin, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "other",
    priority: "medium",
    assignee_id: "",
    affected_device: "",
    affected_url: "",
  });
  const [saving, setSaving] = useState(false);

  const itStaff = staffUsers.filter((u) => u.active && (u.role === "admin" || u.role === "executive_director" || u.is_it_support));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please add a short title for the issue.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        priority: form.priority,
        assignee_id: form.assignee_id || null,
        affected_device: form.affected_device.trim() || null,
        affected_url: form.affected_url.trim() || null,
      };
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket ${data.display_id} logged`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="new-ticket-dialog">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E0]">
          <div>
            <div className="hw-eyebrow text-[10px]">Tech support</div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A] mt-1">Log a ticket</h2>
            <p className="text-xs text-[#334155] mt-1">
              Give us enough detail to get started — you can always add more in the comments.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F9F8F6]" data-testid="new-ticket-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4" data-testid="new-ticket-form">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="new-ticket-title"
              placeholder="e.g., Can't sign into HarborWell on my phone"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Description</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="new-ticket-description"
              placeholder="What were you trying to do? What did you expect? What actually happened? Any error messages?"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="new-ticket-category"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="new-ticket-priority"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Affected device <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
              <input
                value={form.affected_device}
                onChange={(e) => setForm({ ...form, affected_device: e.target.value })}
                data-testid="new-ticket-device"
                placeholder="MacBook · iPhone 15 · iPad mini"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Affected page / URL <span className="normal-case text-[#1E3A8A] font-normal">(optional)</span></label>
              <input
                type="url"
                value={form.affected_url}
                onChange={(e) => setForm({ ...form, affected_url: e.target.value })}
                data-testid="new-ticket-url"
                placeholder="https://…"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              />
            </div>
          </div>

          {isITAdmin && (
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Assign to <span className="normal-case text-[#1E3A8A] font-normal">(default: Eric / IT staff)</span></label>
              <select
                value={form.assignee_id}
                onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
                data-testid="new-ticket-assignee"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
              >
                <option value="">Auto-assign to IT</option>
                {itStaff.map((u) => <option key={u.id} value={u.id}>{u.name}{u.is_it_support ? " (IT)" : ""}</option>)}
              </select>
            </div>
          )}

          <div className="rounded-xl bg-[#E0E7FF] p-3 text-xs text-[#1E3A8A] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <p>
              The assigned IT team member will be notified by email immediately. You'll get a copy of every status change and reply.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#334155]">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="new-ticket-submit"
              className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
            >
              {saving ? "Logging…" : "Log ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
